<script setup lang="ts">
import { IonText, modalController } from "@ionic/vue";
import { computed } from "vue";
import {
  parseISO,
  parse,
  isBefore,
  isEqual,
  format,
  subDays,
  addDays,
} from "date-fns";
import { useI18n } from "@/composables/useI18n";
import DatePickerModal from "@/components/modals/DatePickerModal.vue";
import { getDateFnsLocales } from "@/i18n";
import { useFabriqStore } from "@/store/fabriq";
const props = withDefaults(
  defineProps<{
    icon?: boolean;
    iconOnly?: boolean;
    iconSize?: string;
    readonly?: boolean;
    date?: string | null;
    end?: string | null;
    allowRange?: boolean;
  }>(),
  {
    icon: false,
    iconOnly: false,
    iconSize: "0.875",
    readonly: false,
    allowRange: false,
    end: null,
    date: null,
  }
);
const emit = defineEmits<{
  (e: "date", newDate: { start: string | null; end: string | null }): void;
}>();

const { t } = useI18n();
const fabriqStore = useFabriqStore();

const df = "yyyy-MM-dd";
const now = new Date();
const yesterday = parse(format(subDays(now, 1), df), df, now);
const compare = parse(format(now, df), df, now);
const tomorrow = parse(format(addDays(now, 1), df), df, now);
const color = computed(() => {
  const date = props.end?.length ? props.end : props.date;
  if (!date || !date.length) return "var(--ion-color-primary-shade)";
  const parsed = parse(format(parseISO(date), df), df, now);
  if (isEqual(parsed, compare)) return "var(--ion-color-success)";
  if (isEqual(parsed, tomorrow)) return "var(--ion-color-success)";
  if (isBefore(parsed, compare)) return "var(--ion-color-danger)";
  return "var(--ion-color-primary)";
});
const formated = computed(() => {
  if (!props.date || !props.date.length) {
    return props.iconOnly ? "" : t("datetime.emptyDueDate");
  }
  const parsed = parseISO(props.date);
  if (isEqual(parsed, yesterday)) return t("dates.yesterday");
  if (isEqual(parsed, compare)) return t("dates.today");
  if (isEqual(parsed, tomorrow)) return t("dates.tomorrow");
  return format(parsed, t("formats.dayOfMonth"), {
    locale: getDateFnsLocales(fabriqStore.locale),
  });
});
const endFormated = computed(() => {
  if (!props.end || !props.end.length) {
    return "";
  }
  const parsed = parseISO(props.end);
  if (isEqual(parsed, yesterday)) return t("dates.yesterday");
  if (isEqual(parsed, compare)) return t("dates.today");
  if (isEqual(parsed, tomorrow)) return t("dates.tomorrow");
  return format(parsed, t("formats.dayOfMonth"), {
    locale: getDateFnsLocales(fabriqStore.locale),
  });
});

const selectDate = async () => {
  if (props.readonly) return;
  const modal = await modalController.create({
    component: DatePickerModal,
    canDismiss: true,
    mode: "ios",
    breakpoints: [0, 1],
    initialBreakpoint: 1,
    componentProps: {
      date: props.date,
      end: props.end,
      allowRange: props.allowRange,
      onDone: (dates: { start: string | null; end: string | null }) => {
        modal.dismiss();
        emit("date", dates);
      },
      onCancel: () => {
        modal.dismiss();
      },
    },
  });
  return modal.present();
};
</script>
<template>
  <ion-text class="due-date" @click.stop.prevent="selectDate">
    <font-icon
      v-if="icon"
      :name="'today'"
      :size="iconSize"
      :color="color"
      material
    />
    <span :style="{ color: endFormated.length ? 'inherit' : color }">
      {{ formated }}
    </span>
    <span v-if="endFormated?.length">→</span>
    <span v-if="endFormated?.length" :style="{ color }">{{ endFormated }}</span>
  </ion-text>
</template>

<style scoped>
.due-date {
  padding-top: 2px;
  font-size: var(--font-size-s);
  display: flex;
  align-items: center;
  gap: 0.5em;
}
</style>
